import { DiceTemplateID } from "@kosmod/andy";
import { Loadable, MiniTemplateID, TerrainBrush, TerrainBrushID, TheatreCoreResources, TheatreData } from "..";
import { Mesh } from "three";
import { K3D, Snap, useDisposable } from "@kosmod/graphics-3d";
import { useAsync, useLogger } from "@kosmod/kit";
import { useMemo, useRef } from "@lukekaalim/act";
import { AssetID, AssetReference, GameAssetClient } from "@kosmod/assets";
import { GLTF, GLTFLoader } from "three/examples/jsm/Addons.js";
import * as three from 'three';
import { MiniModelID, MiniSpriteID } from "../defs/miniRender";

type BrushPartObjects = {
  [Key in keyof TerrainBrush["partNames"]]: null | three.Object3D
}

export type TheatreSceneResources = {
  physics: Snap.Engine,

  dieMeshes: Map<DiceTemplateID, Mesh>,
  dieShapes: Map<DiceTemplateID, Snap.Shape>,

  brushParts: Map<TerrainBrushID, BrushPartObjects>,

  miniSpritesURLs: Map<MiniSpriteID, URL>,
  miniSprites: Map<MiniSpriteID, three.Texture>,
  miniModels: Map<MiniModelID, three.Object3D>,

  getModelAsset(reference: AssetReference): null | three.Object3D
};

const getObjectAtPath = (source: three.Object3D, path: string[]): null | three.Object3D => {
  if (path.length < 1)
    return source;

  const [segment, ...childPath] = path;
  const child = source.children.find(c => c.name === segment);
  if (!child)
    return null;

  return getObjectAtPath(child, childPath);
}

export const useTheatreSceneResources = (
  data: TheatreData,
  assets: GameAssetClient,
  coreResource: TheatreCoreResources,
): [TheatreSceneResources | null, Loadable[]] => {
  const { result: physics } = useAsync(() => Snap.Engine.Load(), []);
  const gltfLoader = useRef(() => new GLTFLoader()).get();
  const textureLoader = useRef(() => new three.TextureLoader()).get();

  const logger = useLogger('TheatreSceneResources');

  const modelAssetReferences = [
    ...[...data.diceTemplates.values()].map(template => template.asset),
    ...[...data.brushes.values()].map(brush => brush.modelAsset),
    ...[...data.miniModels.values()].map(miniModel => miniModel.modelAsset),
  ]
  const textureAssetReferences = [
    ...[...data.miniSprites.values()].map(miniSprite => miniSprite.imageAsset),
  ]

  const modelAssetIds = new Set(modelAssetReferences
    .map(ref => ref.type === 'reference' && ref.id)
    .filter((x): x is AssetID => !!x)
  );

  const textureAssetIds = new Set(textureAssetReferences
    .map(ref => ref.type === 'reference' && ref.id)
    .filter((x): x is AssetID => !!x)
  );

  const { result: modelAssets } = useAsync(async () => {
    if (!data.ready)
      return null;
    return new Map(await Promise.all([...modelAssetIds].map(async assetId => {
      const entry = [assetId, await gltfLoader.loadAsync(assets.getAssetURL(assetId).href)] as [AssetID, GLTF]
      return entry;
    })))
  }, [...modelAssetIds, data.ready]);

  const { result: textureAssets } = useAsync(async () => {
    if (!data.ready)
      return null;

    return new Map(await Promise.all([...textureAssetIds].map(async assetId => {
      const texture = await textureLoader.loadAsync(assets.getAssetURL(assetId).href);
      texture.colorSpace = three.SRGBColorSpace;
      const entry = [assetId, texture] as [AssetID, three.Texture]
      return entry;
    })))
  }, [...textureAssetIds, data.ready]);

  const getModelAsset = useMemo(() => (reference: AssetReference) => {
    switch (reference.type) {
      case 'none':
        return null;
      case 'builtin':
        return coreResource.builtin3D[reference.name] || null;
      case 'reference':
        if (!modelAssets)
          return null;
        const gltf = modelAssets.get(reference.id);
        if (!gltf)
          return null;
        return gltf.scene;
    }
  }, [modelAssets, coreResource.builtin3D]);
  const getTextureAsset = useMemo(() => (reference: AssetReference) => {
    switch (reference.type) {
      case 'none':
        return null;
      case 'builtin':
        return coreResource.builtin2D[reference.name]?.texture || null;
      case 'reference':
        if (!textureAssets)
          return null;
        const texture = textureAssets.get(reference.id);
        if (!texture)
          return null;
        return texture;
    }
  }, [textureAssets, coreResource.builtin2D]);
  const getTextureURL = useMemo(() => (reference: AssetReference) => {
    switch (reference.type) {
      case 'none':
        return null;
      case 'builtin':
        return coreResource.builtin2D[reference.name]?.url || null;
      case 'reference':
        return assets.getAssetURL(reference.id);
    }
  }, [assets, coreResource.builtin2D]);

  const dieMeshes = useMemo(() => {
    return new Map([...data.diceTemplates.values()].map(template => {
      const asset = getModelAsset(template.asset);
      if (!asset)
        return null;

      const mesh = asset.children.find(child => child.name === template.modelPath);
      if (!(mesh instanceof three.Mesh))
        return null;

      return [template.id, mesh] as const;

    }).filter(x => !!x))
  }, [getModelAsset, ...(modelAssets && modelAssets.keys() || []), data.diceTemplates])

  const brushParts = useMemo(() => {
    const parts = new Map<TerrainBrushID, BrushPartObjects>();

    for (const brush of data.brushes.values()) {
      const modelAsset = getModelAsset(brush.modelAsset);
      if (!modelAsset)
        continue;

      parts.set(brush.id, {
        floor_4: brush.partNames.floor_4 && getObjectAtPath(modelAsset, brush.partNames.floor_4),
        wall_1: brush.partNames.wall_1 && getObjectAtPath(modelAsset, brush.partNames.wall_1),
        diorama: brush.partNames.diorama && getObjectAtPath(modelAsset, brush.partNames.diorama),
      })
    }

    return parts;
  }, [getModelAsset, data.brushes])

  const {miniSprites, miniSpritesURLs} = useMemo(() => {
    const miniSpriteTextures = new Map<MiniSpriteID, three.Texture>();
    const miniSpritesURLs = new Map<MiniSpriteID, URL>();

    for (const miniSprite of data.miniSprites.values()) {
      const texture = getTextureAsset(miniSprite.imageAsset)
      if (texture)
        miniSpriteTextures.set(miniSprite.id, texture)
      const url = getTextureURL(miniSprite.imageAsset);
      if (url)
        miniSpritesURLs.set(miniSprite.id, url);
    }

    return {
      miniSprites: miniSpriteTextures,
      miniSpritesURLs,
    };
  }, [data.miniSprites, getTextureAsset, getTextureURL])
  const { miniModels } = useMemo(() => {
    const miniModels = new Map<MiniModelID, three.Object3D>();

    for (const miniModel of data.miniModels.values()) {
      const modelAsset = getModelAsset(miniModel.modelAsset)
      if (!modelAsset)
        continue;
      
      const modelAssetPart = getObjectAtPath(modelAsset, miniModel.modelPath);
      if (modelAssetPart)
        miniModels.set(miniModel.id, modelAssetPart);
    }

    return {
      miniModels
    };
  }, [data.miniModels, getModelAsset])

  const { dieShapes } = useDisposable(() => {
    if (!physics || !dieMeshes)
      return { dieShapes: null, dispose() {} };

    const dieShapes = new Map([...data.diceTemplates.values()].map(template => {
      const mesh = dieMeshes.get(template.id);
      if (!mesh)
        return null;

      const geometry = (mesh.geometry as three.BufferGeometry)
        .clone()
        .scale(.2, .2, .2);
      return [template.id, new Snap.GeometryShape(physics, geometry)] as [DiceTemplateID, Snap.Shape]
    }).filter(x => !!x));

    return {
      dieShapes,
      dispose() {
        for (const shape of dieShapes.values()) {
          shape.dispose();
        }
      }
    }
  }, [dieMeshes, physics]);


  const loadables: Loadable[] = [
    ['3DModelAssets', data.contentReady && !!modelAssets && (modelAssetIds.size === modelAssets.size)],
    ['2DTextures', data.contentReady && !!textureAssets && (textureAssetIds.size === textureAssets.size)],
    ['PhysicsAssets', data.contentReady && !!dieShapes && (data.diceTemplates.size === dieShapes.size)],
  ];

  const resources =  useMemo(() => dieShapes && dieMeshes && physics && ({
    physics,
    dieMeshes,
    dieShapes,
    getModelAsset,
    brushParts,
    miniSprites,
    miniModels,
    miniSpritesURLs,
  }) || null, [physics, dieMeshes, dieShapes, getModelAsset, brushParts, miniSprites, miniSpritesURLs, miniModels]);

  return [resources, loadables];
};